import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Logo } from "../../components/Logo";
import { ArrowLeft, FileEdit, ChevronRight } from "lucide-react";

export default function AdminPages() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (user === false) navigate("/admin/login", { replace: true }); }, [user, navigate]);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    api.get("/admin/pages").then((r) => setPages(r.data)).finally(() => setLoading(false));
  }, [user]);

  if (!user) return <div className="py-32 text-center font-mono-label">CHARGEMENT…</div>;

  // Pages already wired to the public site (read content from DB)
  const WIRED = new Set(["coming_soon", "header", "footer"]);

  return (
    <div className="min-h-screen bg-[#FAF8F3]" data-testid="admin-pages">
      <header className="border-b-2 border-[#1A1A18] bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/admin" className="flex items-center gap-2 text-[#1A1A18] hover:text-[#4A7A3D]" data-testid="pages-back-dashboard">
              <ArrowLeft size={16} /> <Logo size="sm" />
            </Link>
            <span className="font-mono-label text-[#4A7A3D] hidden md:inline">CMS · CONTENUS DE PAGES</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="font-mono-label text-[#4A7A3D] mb-3 flex items-center gap-2">
          <FileEdit size={14} /> Pages éditables
        </div>
        <h1 className="font-display text-4xl md:text-5xl mb-3">Contenu des pages</h1>
        <p className="text-[#4B4B48] leading-relaxed mb-10 max-w-2xl">
          Chaque page liste ses textes et images modifiables. Les modifications sont publiées immédiatement.
          Les pages marquées <span className="font-mono-label bg-[#4A7A3D] text-[#FAF8F3] px-1.5 py-0.5 text-[10px]">EN LIGNE</span> sont déjà connectées au site public.
          Les autres pages sont éditables mais leur câblage public sera fait dans une prochaine mise à jour.
        </p>

        {loading ? (
          <p className="font-mono-label">CHARGEMENT…</p>
        ) : (
          <div className="border-2 border-[#1A1A18] divide-y divide-[#E0D8CE]">
            {pages.map((p) => {
              const wired = WIRED.has(p.page);
              return (
                <Link
                  key={p.page}
                  to={`/admin/pages/${p.page}`}
                  className="flex items-center justify-between gap-4 p-5 hover:bg-[#F5EFEB] group"
                  data-testid={`pages-link-${p.page}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <h3 className="font-display text-2xl text-[#1A1A18] group-hover:text-[#4A7A3D]">{p.label}</h3>
                      {wired ? (
                        <span className="font-mono-label bg-[#4A7A3D] text-[#FAF8F3] px-1.5 py-0.5 text-[10px]">EN LIGNE</span>
                      ) : (
                        <span className="font-mono-label bg-[#C4944A] text-[#1A1A18] px-1.5 py-0.5 text-[10px]">À CÂBLER</span>
                      )}
                    </div>
                    <div className="font-mono-label text-[#4B4B48]">{p.field_count} champ{p.field_count > 1 ? "s" : ""} éditable{p.field_count > 1 ? "s" : ""}</div>
                  </div>
                  <ChevronRight size={20} className="text-[#4A7A3D] group-hover:translate-x-1 transition-transform" />
                </Link>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
